import { useRef, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Button } from "@/components/ui/Button";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";

export interface ActionButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  onPress: () => void;
  tooltip: string;
  style?: any;
  disabled?: boolean;
  /** Optional x offset for tooltip positioning */
  tooltipOffsetX?: number;
}

export function ActionButton({ icon, onPress, tooltip, style, disabled, tooltipOffsetX = 0 }: ActionButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>{tooltip}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + tooltipOffsetX, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View ref={ref} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <Button icon={icon} onPress={onPress} style={style} disabled={disabled} />
    </View>
  );
}

export const actionButtonStyles = StyleSheet.create({
  iconBtn: {
    width: 36,
    height: 36,
    backgroundColor: "transparent",
  },
});
